import { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * A <select>-shaped control with a search box at the top of the open menu.
 *
 * Why this exists: several filter option lists are long enough that scrolling
 * to a value is the slow path — 691 universities, 256 nationalities, 37
 * destinations. The reference product puts a type-ahead inside each of those
 * dropdowns; this is the same idea.
 *
 * Semantics the search box MUST NOT touch:
 *
 *   - The internal query only narrows what is *visible*. It never changes
 *     `value`, never fires `onChange`, and is not sent anywhere. The actual
 *     filter changes only when an option is chosen.
 *   - It is cleared whenever the menu closes and whenever the selection
 *     changes, so re-opening always starts from the full list. A stale query
 *     must never hide a valid option on the next open.
 *   - A zero-count option is disabled (it cannot be selected) but still
 *     rendered, and the currently-selected option is always rendered and always
 *     selectable even at count 0 — otherwise a live selection cannot be cleared
 *     from its own control. This mirrors FacetList / IdSelect.
 *
 * `options`: `[{ value, label?, count? }]`. `label` defaults to `value`.
 * `multiple`: when true, `value` is an array and the menu stays open on pick.
 *
 * `loading` (default false): the option list is not ready yet — `/options` is
 * still in flight on first load, or a dependent list is being refetched after a
 * parent changed. The closed control shows "Loading…" with a spinner instead of
 * a bare placeholder, and the open menu says "Loading…" rather than "No
 * options", so a slow filter never reads as an empty or broken one. The control
 * still opens and a value already chosen still shows.
 *
 * `disableZeroCount` (default true): set false for a control that must stay
 * freely changeable no matter what else is selected — University Country and
 * Student Nationality, specifically. Those counts are cross-tabulated against
 * every OTHER active filter (a dependent Course Type, say), so a value this
 * option's OWN field never restricted can still read 0 there — e.g. "United
 * Kingdom" reads 0 once a Course Type with no UK courses is picked. Disabling
 * it then does not grey out a genuinely bad choice; it makes one specific
 * option of the PARENT control unclickable because of a CHILD's selection,
 * which is the exact "I can't change the country" bug this flag exists to
 * rule out. The count can still be shown (informational); it must never
 * remove the ability to pick the option.
 */

/** Fold case and diacritics so "Munchen" matches "München" and "COVENTRY" matches "Coventry". */
const COMBINING_MARKS = /[̀-ͯ]/g;
function fold(s) {
  return String(s ?? '')
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .trim();
}

export function SearchableSelect({
  id,
  label,
  placeholder = 'Any',
  searchPlaceholder = 'Type to filter…',
  options,
  value,
  onChange,
  multiple = false,
  showCounts = true,
  disabled = false,
  disableZeroCount = true,
  loading = false,
  emptyText = 'No options',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0); // keyboard-highlighted row
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const reactId = useId();
  const menuId = `${id ?? reactId}-menu`;

  const selected = multiple ? (value ?? []) : value;
  const isSelected = (v) => (multiple ? selected.includes(v) : selected === v);

  const norm = useMemo(
    () => (options ?? []).map((o) => ({
      value: o.value,
      label: o.label ?? o.value,
      count: o.count,
      _f: fold(o.label ?? o.value),
    })),
    [options],
  );

  // Visible list = query filter, but a selected value is never filtered out.
  const visible = useMemo(() => {
    const q = fold(query);
    if (!q) return norm;
    return norm.filter((o) => o._f.includes(q) || isSelected(o.value));
    // isSelected is derived from `selected`, which is in deps via `value`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [norm, query, value]);

  // Close on click-away and Escape; clear the query whenever the menu closes.
  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    setActive(0);
    // Focusing the search box is the whole point of opening it.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        ref.current?.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Selection changing also resets the query — a fresh open starts clean.
  useEffect(() => { setQuery(''); }, [value]);

  const pick = (v) => {
    if (multiple) {
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
      setQuery('');
      inputRef.current?.focus();
    } else {
      onChange(isSelected(v) ? '' : v);
      setOpen(false);
    }
  };

  const onListKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = visible[active];
      if (o && !(disableZeroCount && o.count === 0 && !isSelected(o.value))) pick(o.value);
    } else if (e.key === 'Home') {
      setActive(0);
    } else if (e.key === 'End') {
      setActive(visible.length - 1);
    }
  };

  // Keep the highlighted row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-i="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // Adjust menu placement if it extends beyond left or right screen edges
  useEffect(() => {
    if (!open || !ref.current) return;
    const menu = ref.current.querySelector('.ss-menu');
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    if (rect.left < 8) {
      menu.style.left = '0';
      menu.style.right = 'auto';
    } else if (rect.right > window.innerWidth - 8) {
      menu.style.left = 'auto';
      menu.style.right = '0';
    }
  }, [open]);

  const summaryText = (() => {
    if (multiple) {
      if (!selected.length) return placeholder;
      if (selected.length === 1) {
        return norm.find((o) => o.value === selected[0])?.label ?? selected[0];
      }
      return `${selected.length} selected`;
    }
    if (!selected) return placeholder;
    return norm.find((o) => o.value === selected)?.label ?? selected;
  })();

  const hasSelection = multiple ? selected.length > 0 : !!selected;

  // Only "loading" when there is genuinely nothing to show yet — once options
  // have arrived, an in-flight refetch just leaves the current list in place.
  const showLoading = loading && norm.length === 0;

  return (
    <div className={label ? 'fld' : undefined}>
      {label && <label htmlFor={`${id ?? reactId}-summary`}>{label}</label>}
      <details
        className="multi-select searchable-select"
        id={id}
        ref={ref}
        open={open}
        onToggle={(e) => !disabled && setOpen(e.currentTarget.open)}
      >
        <summary
          id={`${id ?? reactId}-summary`}
          className={hasSelection ? 'sel' : undefined}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-disabled={disabled || undefined}
          aria-busy={showLoading || undefined}
          role="button"
          onClick={disabled ? (e) => e.preventDefault() : undefined}
        >
          {showLoading && !hasSelection ? (
            <span className="ss-value ss-value-loading">
              <span className="ss-spinner" aria-hidden="true" />
              Loading…
            </span>
          ) : (
            <span className="ss-value">{summaryText}</span>
          )}
        </summary>

        <div className="multi-menu ss-menu" id={menuId} role="listbox" aria-label={label}>
          <div className="ss-search">
            <input
              ref={inputRef}
              type="search"
              className="ss-input"
              placeholder={searchPlaceholder}
              value={query}
              autoComplete="off"
              aria-label={`Filter ${label ?? 'options'}`}
              aria-controls={menuId}
              onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              onKeyDown={onListKey}
            />
          </div>

          <div className="ss-list" ref={listRef}>
            {norm.length === 0 ? (
              <span className="muted ss-note">
                {loading ? (
                  <>
                    <span className="ss-spinner" aria-hidden="true" />
                    Loading…
                  </>
                ) : emptyText}
              </span>
            ) : visible.length === 0 ? (
              <span className="muted ss-note">No matches for “{query}”</span>
            ) : (
              visible.map((o, i) => {
                const sel = isSelected(o.value);
                const off = disableZeroCount && o.count === 0 && !sel;
                return (
                  <button
                    type="button"
                    key={o.value}
                    data-i={i}
                    role="option"
                    aria-selected={sel}
                    disabled={off}
                    className={`multi-option ss-option${off ? ' off' : ''}${sel ? ' sel' : ''}${i === active ? ' active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(o.value)}
                  >
                    {multiple && (
                      <input type="checkbox" readOnly checked={sel} tabIndex={-1} />
                    )}
                    <span className="mo-nm" title={o.label}>{o.label}</span>
                    {showCounts && typeof o.count === 'number' && (
                      <span className="ss-ct">{o.count.toLocaleString()}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {!multiple && hasSelection && (
            <button
              type="button"
              className="ss-clear"
              onClick={() => { onChange(''); setOpen(false); }}
            >
              Clear selection
            </button>
          )}
        </div>
      </details>
    </div>
  );
}
